import type { DocumentData, DocumentReference, Timestamp } from 'firebase/firestore'
import { addDoc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { storage } from '~/firebaseCore'
import { collectionDocuments, documentsQuery } from '~/firestoreWrappers/documentsToSign'
import { DocumentToSignModel, mapDocumentToSign } from '~/models/DocumentToSignModel'
import { errorDefault, savedDefault } from '~/helpers/snackbar'
import { useSnackbarStore } from './snackbar'

export const useDocumentsToSignStore = defineStore('documentToSign', () => {
  const loading: Ref<boolean> = ref(false)
  const error: Ref<unknown | null> = ref(null)
  const data: Ref<DocumentToSignModel[] | null> = ref(null)

  const resetState = () => {
    loading.value = false
    error.value = null
    data.value = null
  }

  const addDocumentToSign = async (document: DocumentToSignModel, userData: any, file: File) => {
    loading.value = true
    error.value = null

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const fileExtension = file.name.split('.').pop()
      const fileName = file.name.split('.')[0]
      const newFileName = `${fileName}-${timestamp}-toSign.${fileExtension}`
      const s = storageRef(storage, `documents/${newFileName}`)
      const uploadResult = await uploadBytes(s, file)
      const fileUrl = await getDownloadURL(uploadResult.ref)

      const newDocument = {
        ...document.toMap(),
        documentPath: fileUrl,
        documentName: newFileName,
        company: userData?.company || null,
        createdByUser: userData?.reference || null,
        lastUpdateByUser: userData?.reference || null,
        lastUpdateTime: serverTimestamp(),
      }

      const onSuccess = (arg0: DocumentReference) => {
        data.value = [...(data.value || []), new DocumentToSignModel(newDocument, arg0)]
        loading.value = false
        error.value = null

        useSnackbarStore().showSnackbar(savedDefault,
        )
      }

      const onError = async (arg: unknown) => {
        useSnackbarStore().showSnackbar(errorDefault(arg),
        )
        try {
          await deleteObject(s)
        }
        catch (deleteErr) {
          useSnackbarStore().showSnackbar(errorDefault(deleteErr),
          )
        }

        loading.value = false
        error.value = arg
      }

      addDoc(
        collectionDocuments,
        newDocument,
      ).then(onSuccess).catch(onError)
    }
    catch (err) {
      loading.value = false
      error.value = err
      useSnackbarStore().showSnackbar(errorDefault(err),
      )
    }
  }

  const signDocument = async (document: DocumentToSignModel, userData: any, file: File, agreementText: string) => {
    loading.value = true
    error.value = null

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const fileExtension = file.name.split('.').pop()
      const fileName = file.name.split('.')[0]
      const newFileName = `${fileName}-${timestamp}-signed.${fileExtension}`
      const s = storageRef(storage, `documents/${newFileName}`)
      const uploadResult = await uploadBytes(s, file)
      const fileUrl = await getDownloadURL(uploadResult.ref)

      const onSuccess = () => {
        loading.value = false
        error.value = null
        useSnackbarStore().showSnackbar(savedDefault)

        data.value = data.value?.map((doc) => {
          if (doc.reference?.id === document.reference?.id) {
            return new DocumentToSignModel({
              ...document.toMap(),
              signedDocumentPath: fileUrl,
              signedDocumentName: newFileName,
              // @ts-expect-error todo
              signedTime: serverTimestamp(),
              status: 'signed',
              agreementText,
            }, doc.reference)
          }
          return doc
        }) ?? null
      }

      const onError = async (arg: unknown) => {
        useSnackbarStore().showSnackbar(errorDefault(arg))
        try {
          await deleteObject(s)
        }
        catch (deleteErr) {
          useSnackbarStore().showSnackbar(errorDefault(deleteErr))
        }
        loading.value = false
        error.value = arg
      }

      if (document.reference && userData?.reference) {
        updateDoc(document.reference, {
          signedDocumentPath: fileUrl,
          signedDocumentName: newFileName,
          lastUpdateByUser: userData?.reference,
          lastUpdateTime: serverTimestamp(),
          signedTime: serverTimestamp(),
          status: 'signed',
        })
          .then(onSuccess)
          .catch(onError)
      }
    }
    catch (err) {
      loading.value = false
      error.value = err
      useSnackbarStore().showSnackbar(errorDefault(err))
    }
  }

  const editDocumentToSign = async (document: DocumentToSignModel, userData: any, file: File | null) => {
    if (!document.reference || !userData.reference) {
      return
    }

    loading.value = true
    error.value = null

    try {
      let fileUrl = document.documentPath
      let newFileName = document.documentName

      if (file !== null) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const fileExtension = file.name.split('.').pop()
        const fileName = file.name.split('.')[0]
        newFileName = `${fileName}-${timestamp}-toSign.${fileExtension}`
        const s = storageRef(storage, `documents/${newFileName}`)

        try {
          await deleteObject(storageRef(storage, `documents/${document.documentName}`))
        }
        catch (deleteErr) {
          useSnackbarStore().showSnackbar(errorDefault(deleteErr))
        }

        const uploadResult = await uploadBytes(s, file)
        fileUrl = await getDownloadURL(uploadResult.ref)
      }

      const onSuccess = () => {
        loading.value = false
        error.value = null
        data.value = data.value?.map((doc) => {
          if (doc.reference?.id === document.reference?.id) {
            return new DocumentToSignModel({
              ...document.toMap(),
              documentPath: fileUrl,
              documentName: newFileName,
            }, doc.reference)
          }
          return doc
        }) ?? null
      }

      const onError = async (arg: unknown) => {
        if (file !== null) {
          try {
            await deleteObject(storageRef(storage, `documents/${newFileName}`))
          }
          catch (deleteErr) {
            useSnackbarStore().showSnackbar(errorDefault(deleteErr))
          }
        }
        loading.value = false
        error.value = arg
      }

      updateDoc(
        document.reference,
        {
          ...document.toMap(),
          documentPath: fileUrl,
          documentName: newFileName,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      ).then(onSuccess).catch(onError)
    }
    catch (err) {
      loading.value = false
      error.value = err
      useSnackbarStore().showSnackbar(errorDefault(err))
    }
  }

  const getDocumentsToSign = (userData: any, dateFrom: Timestamp, dateTo: Timestamp, isManagerOrOwner: boolean = true) => {
    if (!userData?.company)
      return

    loading.value = true
    error.value = null

    const onSuccess = (arg0: DocumentData) => {
      loading.value = false
      error.value = null

      data.value = arg0.docs.map(mapDocumentToSign)
    }

    const onError = (arg: unknown) => {
      loading.value = false
      error.value = arg
      useSnackbarStore().showSnackbar(errorDefault(arg))
    }

    const userRef = isManagerOrOwner ? undefined : userData?.reference
    getDocs(documentsQuery(userData.company, dateFrom, dateTo, userRef)).then(onSuccess).catch(onError)
  }

  const toggleActiveStatus = async (document: DocumentToSignModel, userData: any) => {
    if (!document.reference || !userData.reference) {
      return
    }

    try {
      const newActiveStatus = !document.active

      // Optimistically update the local data first
      if (data.value) {
        const updatedData = data.value.map((doc) => {
          if (doc.reference?.id === document.reference?.id) {
            return new DocumentToSignModel({
              ...doc.toMap(),
              active: newActiveStatus,
            }, doc.reference)
          }
          return doc
        })
        data.value = updatedData
      }

      loading.value = true
      error.value = null

      const onSuccess = () => {
        loading.value = false
        error.value = null
        useSnackbarStore().showSnackbar(savedDefault)
      }

      const onError = (arg: unknown) => {
        // Revert the optimistic update on error
        if (data.value) {
          const revertedData = data.value.map((doc) => {
            if (doc.reference?.id === document.reference?.id) {
              return new DocumentToSignModel({
                ...doc.toMap(),
                active: document.active, // Revert to original value
              }, doc.reference)
            }
            return doc
          })
          data.value = revertedData
        }

        loading.value = false
        error.value = arg
        useSnackbarStore().showSnackbar(errorDefault(arg))
      }

      await updateDoc(
        document.reference,
        {
          active: newActiveStatus,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      ).then(onSuccess).catch(onError)
    }
    catch (err) {
      loading.value = false
      error.value = err
      useSnackbarStore().showSnackbar(errorDefault(err))
    }
  }

  return {
    loading,
    error,
    data,
    resetState,
    addDocumentToSign,
    editDocumentToSign,
    getDocumentsToSign,
    signDocument,
    toggleActiveStatus,
  }
})
